import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import LetterAuthorisationSignatory from '../Models/LetterAuthorisation/LetterAuthorisationSignatory';
import LetterAuthorisationBatchCriteria from '../Models/LetterAuthorisation/Commands/LetterAuthorisationBatchCriteria';
import LetterAuthorisationBatchLookup from '../Models/LetterAuthorisation/Lookups/LetterAuthorisationBatchLookup';
import CreateLetterAuthorisationBatchCommand from '../Models/LetterAuthorisation/Commands/CreateLetterAuthorisationBatchCommand';
import LetterAuthorisationCriteria from '../Models/LetterAuthorisation/Commands/LetterAuthorisationCriteria';
import LetterAuthorisationLookup from '../Models/LetterAuthorisation/Lookups/LetterAuthorisationLookup';
import AuthoriseLetterCommand from '../Models/LetterAuthorisation/Commands/AuthoriseLetterCommand';
import AuthorisationResult from '../Models/LetterAuthorisation/Lookups/AuthorisationResult';
import ExportLetterCommand from '../Models/LetterAuthorisation/Commands/ExportLetterCommand';
import SaveLetterCommand from '../Models/LetterAuthorisation/Commands/SaveLetterCommand';

export interface ILetterAuthorisationApiClient {

    getSignatories(): AxiosPromise<Array<Model.PlainTrackedObject<LetterAuthorisationSignatory>>>;

    saveSignatory(signatory: Model.PartialPlainObject<LetterAuthorisationSignatory>): AxiosPromise<Model.PlainTrackedObject<LetterAuthorisationSignatory>>;

    /**
     * Returns a paged list of authorisation batches.
     */
    getLetterAuthorisationBatchPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<LetterAuthorisationBatchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<LetterAuthorisationBatchLookup>>>;

    /**
     * Gets a list of dates that have not been created for a given report type and criteria.
     */
    getSuggestedBatchDates(criteria: Model.PartialPlainNonTrackedObject<LetterAuthorisationBatchCriteria>): AxiosPromise<Array<Model.PlainObject<LetterAuthorisationBatchLookup>>>;

    /**
     * Creates a letter authorisation batch for the provided criteria.
     */
    createLetterAuthorisationBatch(command: Model.PartialPlainObject<CreateLetterAuthorisationBatchCommand>): AxiosPromise<number>;

    /**
     * Returns a single batch record, with required signatories populated.
     */
    getLetterAuthorisationBatchLookup(letterAuthorisationBatchId: number): AxiosPromise<Model.PlainObject<LetterAuthorisationBatchLookup>>;

    /**
     * Returns a page of authorisation lookups with signatures populated.
     */
    getLetterAuthorisationLookupsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<LetterAuthorisationCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<LetterAuthorisationLookup>>>;

    /**
     * Adds unlinked records of the same type to an existing batch.
     */
    addUnlinkedRecordsToBatch(letterAuthorisationBatchId: number): AxiosPromise;

    /**
     * Authorises a letter.
     */
    authoriseLetter(command: Model.PartialPlainObject<AuthoriseLetterCommand>): AxiosPromise<Model.PlainObject<AuthorisationResult>>;

    /** 
     * Unauthorises a letter, removing the signature for a signatory.
     */
    unAuthoriseLetter(command: Model.PartialPlainObject<AuthoriseLetterCommand>): AxiosPromise;

    /** 
     * Deletes a letter, and deletes the batch if this was the last remaining letter.
     */
    deleteLetter(letterAuthorisationId: number): AxiosPromise;

    /**
     * Authorises the batch.
     */
    authoriseBatch(letterAuthorisationBatchId: number): AxiosPromise;

    /**
     * Unauthorises a batch.
     */
    unAuthoriseBatch(letterAuthorisationBatchId: number): AxiosPromise;

    /**
     * Exports a batch, or letter.
     */
    exportLetters(command: Model.PartialPlainObject<ExportLetterCommand>): AxiosPromise;

    /**
     * Saves a batch, or letter.
     */
    saveLetters(command: Model.PartialPlainObject<SaveLetterCommand>): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class LetterAuthorisationApiClient extends Data.ApiClientBase implements ILetterAuthorisationApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/letter-authorisation`);
    }

    public getSignatories(): AxiosPromise<Array<Model.PlainTrackedObject<LetterAuthorisationSignatory>>> {
        return this.axios.get(`${this.apiPath}/signatories`);
    }

    public saveSignatory(signatory: Model.PartialPlainObject<LetterAuthorisationSignatory>): AxiosPromise<Model.PlainTrackedObject<LetterAuthorisationSignatory>> {
        return this.axios.post(`${this.apiPath}/signatories`, signatory);
    }

    public getLetterAuthorisationBatchPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<LetterAuthorisationBatchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<LetterAuthorisationBatchLookup>>> {
        return this.axios.get(`${this.apiPath}/batches-paged?${Utils.getQueryString(pageRequest)}`);
    }

    public getSuggestedBatchDates(criteria: Model.PartialPlainNonTrackedObject<LetterAuthorisationBatchCriteria>): AxiosPromise<Array<Model.PlainObject<LetterAuthorisationBatchLookup>>> {
        return this.axios.get(`${this.apiPath}/suggested-batches?${Utils.getQueryString(criteria)}`);
    }

    public createLetterAuthorisationBatch(command: Model.PartialPlainObject<CreateLetterAuthorisationBatchCommand>): AxiosPromise<number> {
        return this.axios.post(`${this.apiPath}/create-batch`, command);
    }

    public getLetterAuthorisationBatchLookup(letterAuthorisationBatchId: number): AxiosPromise<Model.PlainObject<LetterAuthorisationBatchLookup>> {
        return this.axios.get(`${this.apiPath}/batch/${letterAuthorisationBatchId}`);
    }

    public getLetterAuthorisationLookupsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<LetterAuthorisationCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<LetterAuthorisationLookup>>> {
        return this.axios.get(`${this.apiPath}/letters-paged?${Utils.getQueryString(request)}`);
    }

    public addUnlinkedRecordsToBatch(letterAuthorisationBatchId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/batch/${letterAuthorisationBatchId}/add-unlinked`);
    }

    public authoriseLetter(command: Model.PartialPlainObject<AuthoriseLetterCommand>): AxiosPromise<Model.PlainObject<AuthorisationResult>> {
        return this.axios.post(`${this.apiPath}/authorise-letter`, command);
    }

    public unAuthoriseLetter(command: Model.PartialPlainObject<AuthoriseLetterCommand>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/unauthorise-letter`, command);
    }

    public deleteLetter(letterAuthorisationId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/letter/${letterAuthorisationId}`);
    }

    public authoriseBatch(letterAuthorisationBatchId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/authorise-batch/${letterAuthorisationBatchId}`);
    }

    public unAuthoriseBatch(letterAuthorisationBatchId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/unauthorise-batch/${letterAuthorisationBatchId}`);
    }

    public exportLetters(command: Model.PartialPlainObject<ExportLetterCommand>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/export`, command);
    }

    public saveLetters(command: Model.PartialPlainObject<SaveLetterCommand>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/save-pdf`, command);
    }

    // Client only properties / methods
}